import React, { useEffect, useState } from "react";
import { Bell, BellOff, BellRing } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

/** Web push subscribe / unsubscribe button. Mounted on the dashboard so users
 *  can opt in to the daily AI morning brief. Soft-fails on browsers without
 *  the Push API (older Safari, Firefox in private mode). */
export default function PushSubscribeButton() {
  const [supported, setSupported] = useState(true);
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [vapidKey, setVapidKey] = useState(null);

  useEffect(() => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      setSupported(false);
      return;
    }
    api.get("/push/public-key").then((r) => setVapidKey(r.data.public_key)).catch(() => {});
    navigator.serviceWorker.ready.then((reg) => reg.pushManager.getSubscription())
      .then((sub) => setSubscribed(!!sub))
      .catch(() => {});
  }, []);

  if (!supported) return null;

  const subscribe = async () => {
    if (!vapidKey) { toast.error("Push not configured"); return; }
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") { toast.error("Notifications blocked"); return; }
      const reg = await navigator.serviceWorker.ready;
      // If there's a stale subscription (e.g. left over from a previous
      // VAPID key), drop it first — otherwise pushManager.subscribe throws
      // "subscribe() called on non-active service worker".
      const existing = await reg.pushManager.getSubscription();
      if (existing) {
        try { await existing.unsubscribe(); } catch { /* best effort */ }
      }
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      });
      const json = sub.toJSON();
      await api.post("/push/subscribe-web", {
        endpoint: json.endpoint,
        keys: json.keys,
      });
      setSubscribed(true);
      toast.success("You'll get your morning brief at 08:00 UTC");
    } catch (e) {
      // Surface the real browser error so we stop flying blind on the
      // generic "Couldn't subscribe" toast. Most common causes: user denied
      // the OS-level notification permission, enterprise policy blocks push,
      // or we're in a private/incognito window.
      console.error("[push] subscribe failed:", e);
      const msg = (e && e.message) || "Couldn't subscribe";
      toast.error(msg.length > 140 ? "Couldn't subscribe — check browser settings" : msg);
    } finally { setBusy(false); }
  };

  const unsubscribe = async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        const json = sub.toJSON();
        await api.post("/push/unsubscribe-web", { endpoint: json.endpoint, keys: json.keys });
        await sub.unsubscribe();
      }
      setSubscribed(false);
      toast.success("Notifications turned off");
    } finally { setBusy(false); }
  };

  const test = async () => {
    setBusy(true);
    try {
      const r = await api.post("/push/test");
      toast.success(`Test sent (web=${r.data.sent_web}, mobile=${r.data.sent_expo})`);
    } catch { toast.error("Test failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="flex items-center gap-2">
      <button
        onClick={subscribed ? unsubscribe : subscribe}
        disabled={busy}
        data-testid="push-toggle"
        className="pp-btn-secondary px-3 py-2 rounded-xl text-xs flex items-center gap-2 disabled:opacity-50"
      >
        {subscribed ? <BellRing size={14} className="text-[#00E676]" /> : <Bell size={14} />}
        {subscribed ? "Notifications on" : "Get morning brief"}
      </button>
      {subscribed && (
        <button
          onClick={test}
          disabled={busy}
          data-testid="push-test"
          className="text-xs text-zinc-500 hover:text-white"
          title="Send a test notification"
        >
          Test
        </button>
      )}
    </div>
  );
}

/** RFC-7515 base64url → Uint8Array, matching the format the W3C Push API
 *  expects for `applicationServerKey`. */
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}
